import moment from 'moment'
import creditCardType from 'credit-card-type'

export function throttle(func, delay) {
  let timeout = null

  return args => {
    if (!timeout) {
      timeout = setTimeout(() => {
        func(args)
        timeout = null
      }, delay)
    }
  }
}

export const validateEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const serialize = obj => {
  let arr = []
  for (let p in obj)
    if (obj.hasOwnProperty(p)) {
      arr.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
    }
  return arr.join('&')
}

export const isEmpty = obj => {
  return (
    !obj || (obj.constructor === Object && Object.entries(obj).length === 0)
  )
}

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Date formatting utilities
export const getFormatedDateForApp = d => {
  if (!d) return ''
  const m = moment(d, 'YYYY-MM-DD')
  return m.format('DD/MM/YYYY')
}

export const getFormatedDateForCRM = (d, short_format = false) => {
  if (d == null) return ''

  if (short_format) {
    return moment(d).format('MM/YY')
  }

  return moment(d).format('YYYY-MM-DD').toString()
}

// Credit card number formatting
export const formatCreditCardNumber = input => {
  if (!input) return input

  // Remove any existing spaces and non-numeric characters from the input
  const cleanedInput = input.replace(/\D/g, '')

  // Add space after every 4 digits using regex
  const formattedInput = cleanedInput.replace(/(\d{4})(?=\d)/g, '$1 ')

  return formattedInput
}

// Date validation utilities
export const checkExpiryDate = (issue_date, expiry_date) => {
  if (!issue_date || !expiry_date) return false

  const m1 = moment(issue_date, 'YYYY-MM-DD')
  const m2 = moment(expiry_date)

  return m1.unix() < m2.unix()
}

export const checkTransactionDate = (
  date_of_visit,
  date_of_transaction,
  rawvalue
) => {
  if (rawvalue && rawvalue.length < 10) {
    return 'improper_date'
  }

  if (!date_of_visit || !date_of_transaction) return false

  const m1 = moment(date_of_visit, 'YYYY-MM-DD')
  const m2 = moment(date_of_transaction)

  return m1.unix() <= m2.unix()
}

export const checkEqualDateofBirth = (d1, d2) => {
  if (!d1 || !d2) return false

  const m1 = moment(d1, 'YYYY-MM-DD')
  const m2 = moment(d2)

  m2.startOf('day')

  return m1.unix() === m2.unix()
}

export const checkDateLessThanToday = input_date => {
  if (!input_date) return false

  const m1 = moment(input_date, 'DD/MM/YYYY')
  const m2 = moment()

  return m1.unix() <= m2.unix()
}

export const checkDateGreaterThanEqualTo = (d1, d2) => {
  if (!d1 || !d2) return false

  const m1 = moment(d1, 'YYYY/MM/DD')
  const m2 = moment(d2, 'YYYY/MM/DD')

  return m1.unix() >= m2.unix()
}

// File preparation utility
export const prepareFileData = (array = [], filterFiles = []) => {
  return array.reduce((acc, item) => {
    const { file_obj } = item
    const isFiltered = filterFiles.includes(file_obj?.id)

    if (!isFiltered) {
      acc.push({
        file_no: acc.length,
        file: {},
        id: generateUUID(),
        fileName: file_obj?.name || '',
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: true,
        isError: false,
        show: true,
        file_obj: file_obj || {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: null
        }
      })
    }
    return acc
  }, [])
}

// Cookie utility
export const getCookie = name => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

// Utility function to map currency values
export const mapCurrencyValue = currency => {
  const lowerCurrency = currency.toLowerCase()
  if (lowerCurrency === 'peso') return 'MXN'
  if (lowerCurrency === 'usd') return 'USD'
  if (lowerCurrency === 'mxn') return 'MXN'
  if (lowerCurrency === 'dollar') return 'USD'
  return currency.toUpperCase()
}

// Utility function to format IVA value
export const formatIVAValue = (ivaValue, defaultValue = '16') => {
  if (!ivaValue) return defaultValue
  // Convert decimal values like "16.00000000" to "16"
  const numericValue = parseFloat(ivaValue)
  if (isNaN(numericValue)) return defaultValue
  return numericValue.toString()
}

// Utility function to format currency values to 2 decimal places
export const formatCurrencyValue = (value, defaultValue = '0.00') => {
  if (!value) return defaultValue
  const numericValue = parseFloat(value)
  if (isNaN(numericValue)) return defaultValue
  return numericValue.toFixed(2)
}

/** True when cost is a numeric zero (e.g. "0.00", "0.00000000", 0). Empty/unset is not zero. */
export const isZeroCost = cost => {
  if (cost === null || cost === undefined || cost === '') return false
  const n = parseFloat(cost)
  return !Number.isNaN(n) && n === 0
}

export const setCreditCardNumber = (cardNumber, lastDigits) => {
  const value = cardNumber && cardNumber?.slice(-4)
  if (value === lastDigits) {
    return cardNumber
  }
  return null
}

// Utility function to format card number with masking
export const formatCardNumberForDisplay = cardNumber => {
  if (!cardNumber) return ''
  const lastFourDigits = cardNumber.slice(-4)
  return `***************${lastFourDigits}`
}

export const getCreditCardType = value_array => {
  var value = value_array.join('')
  var cc_type = creditCardType(value)[0]

  if (cc_type !== undefined) {
    cc_type = cc_type.type
  }

  if (value.length == 0) {
    cc_type = ''
  }

  return cc_type
}

// Format card type from BIN validation (kebab-case) to proper title case for API payload
export const formatCardTypeForPayload = cardType => {
  if (!cardType) return ''

  // Mapping of kebab-case to proper title case format
  const cardTypeMap = {
    'visa': 'Visa',
    'mastercard': 'Mastercard',
    'american-express': 'American Express',
    'discover': 'Discover',
    'jcb': 'JCB',
    'diners-club': 'Diners Club',
    'maestro': 'Maestro',
    'unionpay': 'UnionPay',
    'rupay': 'RuPay'
  }

  // Check if we have a direct mapping
  const lowerCaseType = cardType.toLowerCase()
  if (cardTypeMap[lowerCaseType]) {
    return cardTypeMap[lowerCaseType]
  }

  // Fallback: convert kebab-case to title case
  return cardType
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const SERVER_OWNED_CARD_FIELDS = [
  'card_number',
  'luhn_check',
  'bin_check',
  'is_manual',
  'credit_card_token',
  'redaction_verified',
  'redaction_message',
  'encrypted_filename'
]

export const stripServerOwnedCardFields = (attributes = {}) => {
  const sanitized = { ...attributes }

  SERVER_OWNED_CARD_FIELDS.forEach(field => {
    delete sanitized[field]
  })
  if (!attributes.card_number) {
    delete sanitized.name
  }

  return sanitized
}

export const formatDate = dateString => {
  if (!dateString || dateString === '' || dateString === false) {
    return ''
  }

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  } catch (error) {
    return dateString
  }
}

// Helper function to calculate financial values based on currency
const calculateFinancialValues = (
  itemsTotal,
  currency,
  exchangeRate,
  ivaRate
) => {
  const isUSD = currency?.toLowerCase() === 'usd'
  const iva = parseFloat(ivaRate) || 16
  const rate = parseFloat(exchangeRate) || 1

  let sub_total, taxAmount, taxMxn, refundAmount, serviceCharges, totalPurchase

  // Calculate subtotal and tax based on IVA
  if (iva === 16) {
    sub_total = (itemsTotal / 1.16).toFixed(2)
    taxAmount = (itemsTotal - sub_total).toFixed(2)
  } else if (iva === 8) {
    sub_total = (itemsTotal / 1.08).toFixed(2)
    taxAmount = (itemsTotal - sub_total).toFixed(2)
  } else {
    sub_total = (itemsTotal / 1.16).toFixed(2) // Default to 16%
    taxAmount = (itemsTotal - sub_total).toFixed(2)
  }

  if (isUSD) {
    // For USD: convert tax to MXN for refund calculations
    taxMxn = (taxAmount * rate).toFixed(2)
    refundAmount = (taxMxn * 0.6525).toFixed(2)
    serviceCharges = (taxMxn * 0.3475).toFixed(2)
    totalPurchase = (itemsTotal * rate).toFixed(2)
  } else {
    // For MXN: tax is already in MXN
    taxMxn = taxAmount
    refundAmount = (taxAmount * 0.6525).toFixed(2)
    serviceCharges = (taxAmount * 0.3475).toFixed(2)
    totalPurchase = itemsTotal.toFixed(2)
  }

  return {
    sub_total,
    tax: taxMxn,
    refund_to_customer: refundAmount,
    service_charges: serviceCharges,
    dollar_total: isUSD ? itemsTotal : null,
    total_purchase: totalPurchase,
    exchange_rate: isUSD ? rate : null
  }
}

export const collectDroppedFileIds = state => {
  const droppedFileIds = new Set()

  try {
    // Collect passport files
    const passportFiles = state.passport?.files || []
    passportFiles.forEach(file => {
      if (file?.file_obj?.id || file?.id) {
        droppedFileIds.add(file.file_obj?.id || file?.id)
      }
      if (file?.id) {
        droppedFileIds.add(file.id)
      }
      if (file?.file_obj?.document_revision_id || file?.document_revision_id) {
        droppedFileIds.add(
          file.file_obj?.document_revision_id || file?.document_revision_id
        )
      }
    })

    // Collect trip air files
    const tripAirFiles = state.tripAir?.files || {}
    Object.keys(tripAirFiles).forEach(fileType => {
      const files = tripAirFiles[fileType] || []
      files.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })
    })

    // Collect trip sea files
    const tripSeaFiles = state.tripSea?.files || {}
    Object.keys(tripSeaFiles).forEach(fileType => {
      const files = tripSeaFiles[fileType] || []
      files.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })
    })

    // Collect payment card files
    const paymentCards =
      state.paymentCards?.paymentCards || state?.paymentCards || []
    paymentCards.forEach(card => {
      const files = card?.files || []
      files.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })
    })

    // Collect credit card files (legacy support)
    const creditCards =
      state.paymentCards?.creditCards || state?.creditCards || []
    creditCards.forEach(card => {
      const files = card?.files || []
      files.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })
    })

    // Collect purchase receipt files
    const purchaseReceipt = state.purchaseReceipt || {}

    // Purchase receipts (pr array)
    const purchaseReceiptsArray = purchaseReceipt.pr || []
    purchaseReceiptsArray.forEach(pr => {
      // POS receipt files in each purchase receipt
      const prPosFiles = pr.files?.pos_receipt || []
      prPosFiles.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })

      // SAT invoice files in each purchase receipt
      const prSatFiles = pr.files?.sat_invoice || []
      prSatFiles.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })

      // POS Stamp files in each purchase receipt (array of cards → array of arrays)
      const prStampCards = pr.files?.purchase_stamp || []
      prStampCards.forEach(cardFiles => {
        if (Array.isArray(cardFiles)) {
          cardFiles.forEach(file => {
            if (file?.file_obj?.id || file?.id) {
              droppedFileIds.add(file.file_obj?.id || file?.id)
            }
            if (file?.id) {
              droppedFileIds.add(file.id)
            }
            if (
              file?.file_obj?.document_revision_id ||
              file?.document_revision_id
            ) {
              droppedFileIds.add(
                file.file_obj?.document_revision_id ||
                file?.document_revision_id
              )
            }
          })
        }
      })


      // Purchase others files in each purchase receipt
      const prPurchaseOthersFiles = pr.files?.purchase_others || []
      prPurchaseOthersFiles.forEach(file => {
        if (file?.file_obj?.id || file?.id) {
          droppedFileIds.add(file.file_obj?.id || file?.id)
        }
        if (file?.id) {
          droppedFileIds.add(file.id)
        }
        if (
          file?.file_obj?.document_revision_id ||
          file?.document_revision_id
        ) {
          droppedFileIds.add(
            file.file_obj?.document_revision_id || file?.document_revision_id
          )
        }
      })

      // Payment method files in each purchase receipt
      const paymentMethods = pr.payment_methods || []
      paymentMethods.forEach(pm => {
        // Card files
        const cardFiles = pm.files?.card || []
        cardFiles.forEach(file => {
          if (file?.file_obj?.id || file?.id) {
            droppedFileIds.add(file.file_obj?.id || file?.id)
          }
          if (file?.id) {
            droppedFileIds.add(file.id)
          }
          if (
            file?.file_obj?.document_revision_id ||
            file?.document_revision_id
          ) {
            droppedFileIds.add(
              file.file_obj?.document_revision_id || file?.document_revision_id
            )
          }
        })

        // Slip files
        const slipFiles = pm.files?.slip || []
        slipFiles.forEach(file => {
          if (file?.file_obj?.id || file?.id) {
            droppedFileIds.add(file.file_obj?.id || file?.id)
          }
          if (file?.id) {
            droppedFileIds.add(file.id)
          }
          if (
            file?.file_obj?.document_revision_id ||
            file?.document_revision_id
          ) {
            droppedFileIds.add(
              file.file_obj?.document_revision_id || file?.document_revision_id
            )
          }
        })
      })
    })

    // Collect misc document files
    const miscDocs = state.misc_douments?.misc || []
    miscDocs.forEach(doc => {
      const files = doc?.files || []
      // Only collect IDs if there are actual files in the files array
      if (files.length > 0) {
        files.forEach(file => {
          if (file?.file_obj?.id || file?.id) {
            droppedFileIds.add(file.file_obj?.id || file?.id)
          }
          if (file?.id) {
            droppedFileIds.add(file.id)
          }
          if (
            file?.file_obj?.document_revision_id ||
            file?.document_revision_id
          ) {
            droppedFileIds.add(
              file.file_obj?.document_revision_id || file?.document_revision_id
            )
          }
        })
      }
    })
    const arnFiles = state.arn_files?.arn || []
    arnFiles.forEach(doc => {
      const files = doc?.files || []
      // Only collect IDs if there are actual files in the files array
      if (files.length > 0) {
        files.forEach(file => {
          if (file?.file_obj?.id || file?.id) {
            droppedFileIds.add(file.file_obj?.id || file?.id)
          }
          if (file?.id) {
            droppedFileIds.add(file.id)
          }
          if (
            file?.file_obj?.document_revision_id ||
            file?.document_revision_id
          ) {
            droppedFileIds.add(
              file.file_obj?.document_revision_id || file?.document_revision_id
            )
          }
        })
      }
    })

    // Collect GB Application files
    const gbApplications = state.gb_application_files?.gb_application || []
    gbApplications.forEach(doc => {
      const files = doc?.files || []
      // Only collect IDs if there are actual files in the files array
      if (files.length > 0) {
        files.forEach(file => {
          if (file?.file_obj?.id || file?.id) {
            droppedFileIds.add(file.file_obj?.id || file?.id)
          }
          if (file?.id) {
            droppedFileIds.add(file.id)
          }
          if (
            file?.file_obj?.document_revision_id ||
            file?.document_revision_id
          ) {
            droppedFileIds.add(
              file.file_obj?.document_revision_id || file?.document_revision_id
            )
          }
        })
      }
    })

    return droppedFileIds
  } catch (error) {
    console.error('Error collecting dropped file IDs:', error)
    return new Set()
  }
}

export const collectDroppedFileNames = state => {
  const names = new Set()

  const addName = file => {
    const raw =
      file?.file_obj?.name ??
      file?.file_obj?.file_name ??
      file?.fileName ??
      file?.file_name ??
      file?.name ??
      ''
    if (typeof raw === 'string' && raw.trim()) {
      names.add(raw.trim())
    }
  }

  try {
    const passportFiles = state?.passport?.files || []
    passportFiles.forEach(addName)

    const tripAirFiles = state?.tripAir?.files || {}
    Object.keys(tripAirFiles).forEach(fileType => {
      ; (tripAirFiles[fileType] || []).forEach(addName)
    })

    const tripSeaFiles = state?.tripSea?.files || {}
    Object.keys(tripSeaFiles).forEach(fileType => {
      ; (tripSeaFiles[fileType] || []).forEach(addName)
    })

    const paymentCards =
      state?.paymentCards?.paymentCards || state?.paymentCards || []
    paymentCards.forEach(card => {
      ; (card?.files || []).forEach(addName)
    })

    const creditCards =
      state?.paymentCards?.creditCards || state?.creditCards || []
    creditCards.forEach(card => {
      ; (card?.files || []).forEach(addName)
    })

    const purchaseReceiptsArray = state?.purchaseReceipt?.pr || []
    purchaseReceiptsArray.forEach(pr => {
      ; (pr?.files?.pos_receipt || []).forEach(addName)
        ; (pr?.files?.sat_invoice || []).forEach(addName)
        ; (pr?.files?.purchase_others || []).forEach(addName)
        ; (pr?.files?.purchase_stamp || []).forEach(cardFiles => {
          if (Array.isArray(cardFiles)) cardFiles.forEach(addName)
        })
        ; (pr?.payment_methods || []).forEach(pm => {
          ; (pm?.files?.card || []).forEach(addName)
            ; (pm?.files?.slip || []).forEach(addName)
        })
    })

    const miscDocs = state?.misc_douments?.misc || []
    miscDocs.forEach(doc => {
      ; (doc?.files || []).forEach(addName)
    })

    const arnFiles = state?.arn_files?.arn || []
    arnFiles.forEach(doc => {
      ; (doc?.files || []).forEach(addName)
    })

    const gbApplications = state?.gb_application_files?.gb_application || []
    gbApplications.forEach(doc => {
      ; (doc?.files || []).forEach(addName)
    })

    return names
  } catch (error) {
    console.error('Error collecting dropped file names:', error)
    return new Set()
  }
}
